/**
 * API 클라이언트
 * 백엔드 API와 통신을 담당하는 클라이언트
 *
 * @see SPEC-192-i18n-api.md - Accept-Language 헤더 지원
 * @see #129 - Sanctum SPA 인증 지원 (credentials: 'include')
 */

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  detectBrowserLocale,
  isValidLocale,
  type Locale,
} from '@/config/locales';
import { logger } from '@/utils/logger';
import { annotateTransportFailure, annotateParseFailure, annotateParseRequest, isReadRequestId, READ_REQUEST_ID_HEADER, recordPartyParseFailure, type SafeReadOperation, type TransportPhase } from '@/utils/transportDiagnostics';
import {
  getCheckoutTableSessionFromStorage,
  getTableSessionFromStorage,
  isValidTableContext,
} from '@/lib/tableContext';
import {
  getStatusMessage,
  getParseFallbackMessage,
  getGenericFallbackMessage,
} from './error-messages';

const LEGACY_STAGING_API_HOSTNAME = 'admin.staging.olulo.com.mx';
const DEV_API_ENDPOINT = 'https://admin.dev.olulo.com.mx';
const DEV_API_HOSTNAME = 'admin.dev.olulo.com.mx';
const DEV_MENU_HOSTNAME = 'menu.dev.olulo.com.mx';
const PROD_API_ENDPOINT = 'https://admin.olulo.com.mx';
const PROD_API_HOSTNAME = 'admin.olulo.com.mx';
const PROD_MENU_HOSTNAME = 'menu.olulo.com.mx';
const STAGING_API_ENDPOINT = 'https://admin.stage.olulo.com.mx';
const STAGING_API_HOSTNAME = 'admin.stage.olulo.com.mx';
const STAGING_MENU_HOSTNAME = 'menu.staging.olulo.com.mx';
const VERCEL_PROJECT_PREVIEW_HOST_SUFFIX = '-mx-olulo.vercel.app';
const VERCEL_MAIN_PREVIEW_HOST_SUFFIX = '-git-main-mx-olulo.vercel.app';
const CHECKOUT_PATH_PATTERN = /^\/checkout\/?$/;
const TABLE_CHECKOUT_PATH_PATTERN = /^\/restaurant\/[^/]+\/table\/[^/]+\/checkout\/?$/;

function isCheckoutRoutePath(pathname: string): boolean {
  return CHECKOUT_PATH_PATTERN.test(pathname) || TABLE_CHECKOUT_PATH_PATTERN.test(pathname);
}

function normalizeApiEndpoint(endpoint: string | undefined): string {
  return endpoint?.trim().replace(/\/+$/, '') ?? '';
}

function getRuntimeHostname(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.hostname;
}

function isLegacyStagingApiEndpoint(endpoint: string): boolean {
  try {
    return new URL(endpoint).hostname === LEGACY_STAGING_API_HOSTNAME;
  } catch {
    return false;
  }
}

function isStagingApiEndpoint(endpoint: string): boolean {
  try {
    return new URL(endpoint).hostname === STAGING_API_HOSTNAME;
  } catch {
    return false;
  }
}

function isDevelopmentApiEndpoint(endpoint: string): boolean {
  try {
    return new URL(endpoint).hostname === DEV_API_HOSTNAME;
  } catch {
    return false;
  }
}

function isProductionApiEndpoint(endpoint: string): boolean {
  try {
    return new URL(endpoint).hostname === PROD_API_HOSTNAME;
  } catch {
    return false;
  }
}

function isNonMainVercelPreviewHost(hostname: string): boolean {
  return (
    hostname.endsWith(VERCEL_PROJECT_PREVIEW_HOST_SUFFIX) &&
    !hostname.endsWith(VERCEL_MAIN_PREVIEW_HOST_SUFFIX)
  );
}

function isMainVercelPreviewHost(hostname: string): boolean {
  return hostname.endsWith(VERCEL_MAIN_PREVIEW_HOST_SUFFIX);
}

function isStagingRuntimeHost(hostname: string): boolean {
  return hostname === STAGING_MENU_HOSTNAME || isNonMainVercelPreviewHost(hostname);
}

function isDevelopmentRuntimeHost(hostname: string): boolean {
  return (
    hostname === DEV_MENU_HOSTNAME ||
    hostname === DEV_API_HOSTNAME ||
    isMainVercelPreviewHost(hostname)
  );
}

export function resolveApiBaseUrl(
  configuredEndpoint: string | undefined,
  hostname: string = getRuntimeHostname()
): string {
  const endpoint = normalizeApiEndpoint(configuredEndpoint);

  if (isDevelopmentRuntimeHost(hostname) && !isDevelopmentApiEndpoint(endpoint)) {
    return DEV_API_ENDPOINT;
  }

  if (hostname === PROD_MENU_HOSTNAME && !isProductionApiEndpoint(endpoint)) {
    return PROD_API_ENDPOINT;
  }

  if (isStagingRuntimeHost(hostname) && !isStagingApiEndpoint(endpoint)) {
    return STAGING_API_ENDPOINT;
  }

  if (isLegacyStagingApiEndpoint(endpoint)) {
    return STAGING_API_ENDPOINT;
  }

  return endpoint;
}

const RAW_API_BASE_URL = import.meta.env.VITE_API_ENDPOINT;
const CONFIGURED_API_BASE_URL = normalizeApiEndpoint(RAW_API_BASE_URL);
const API_BASE_URL = resolveApiBaseUrl(RAW_API_BASE_URL);

/**
 * URL 경로 파라미터 검증 정규식
 * 허용: 알파벳, 숫자, @, -, _, .
 * 금지: /, .., 특수문자 등 (경로 조작 방지)
 */
const SAFE_PATH_PARAM_REGEX = /^[@a-zA-Z0-9_.-]+$/;

/**
 * URL 경로 파라미터 검증
 * @throws 유효하지 않은 파라미터일 경우 에러
 */
export function validatePathParam(param: string, paramName: string = 'parameter'): string {
  if (!param || typeof param !== 'string') {
    throw new ApiClientError(`${paramName}이(가) 비어있습니다`, 400, 'INVALID_PARAM');
  }

  // 경로 조작 시도 차단
  if (param.includes('..') || param.includes('/') || param.includes('\\')) {
    throw new ApiClientError(`${paramName}에 유효하지 않은 문자가 포함되어 있습니다`, 400, 'INVALID_PARAM');
  }

  if (!SAFE_PATH_PARAM_REGEX.test(param)) {
    throw new ApiClientError(`${paramName} 형식이 올바르지 않습니다`, 400, 'INVALID_PARAM');
  }

  return encodeURIComponent(param);
}

if (!CONFIGURED_API_BASE_URL) {
  logger.error(
    '❌ API 엔드포인트가 설정되지 않았습니다. ' +
      '.env.local 파일에서 VITE_API_ENDPOINT를 설정해주세요.'
  );
} else if (!API_BASE_URL) {
  logger.error(
    '❌ API 엔드포인트를 확인할 수 없습니다. ' +
      '.env.local 파일에서 VITE_API_ENDPOINT를 확인해주세요.'
  );
} else if (API_BASE_URL !== CONFIGURED_API_BASE_URL) {
  logger.warn(
    `[ApiClient] 현재 실행 호스트에 맞춰 API 엔드포인트를 보정했습니다. 사용 URL: ${API_BASE_URL}`
  );
}

export const DEFAULT_TIMEOUT = 10000; // 10초

/**
 * #380 Stage 2 — 동적 i18n fallback (ko/es/en).
 *
 * Stage 1 의 es-MX 정적 상수는 error-messages.ts 의 다국어 표로 흡수.
 * AppContext 가 locale 변경 시 setApiErrorLanguage 로 동기화 →
 * client.ts handleResponse 가 항상 사용자 언어로 응답.
 */

/**
 * 한국어 검출 정규식 — 비매핑 status (422 등) 에서 BE 가 한국어 server message 를
 * 내려주면 generic fallback 으로 대체하기 위해 사용 (codex 1R P1).
 */
const HANGUL_REGEX = /[ㄱ-ㆎ가-힣]/;

export interface ApiError {
  message: string;
  status: number;
  code?: string;
}

/**
 * ApiClientError.body 보존 상한 (JSON 직렬화 길이, UTF-16 code unit 기준).
 *
 * #674 codex P2 — 에러 본문을 무제한 메모리 보존하지 않도록 캡.
 * 정상 BE envelope (점유 409 등) 는 수 KB 수준이라 영향 없음.
 */
const MAX_ERROR_BODY_CHARS = 64 * 1024;

/**
 * 에러 본문 보존 가드 — 직렬화 길이가 상한 이내일 때만 보존.
 * 직렬화 불가(이론상 JSON.parse 산출물엔 없음)·상한 초과 시 undefined.
 */
function capErrorBody(errorData: unknown): unknown {
  try {
    const serialized = JSON.stringify(errorData);
    if (typeof serialized === 'string' && serialized.length <= MAX_ERROR_BODY_CHARS) {
      return errorData;
    }
  } catch {
    // 직렬화 실패 — 보존하지 않음
  }
  return undefined;
}

export class ApiClientError extends Error {
  status: number;
  code?: string;
  /** 서버 JSON 응답에서 파싱된 메시지인지 여부 (false면 클라이언트 fallback 메시지) */
  isServerMessage: boolean;
  /**
   * 서버 에러 응답 본문 (JSON 파싱 성공 + 크기 상한 이내일 때 원본 그대로).
   *
   * #674 — 점유 테이블 409 처럼 BE 가 `data.active_session` 등 구조화 payload 를
   * 에러 본문에 담아 보내는 경우, message 로 뭉개지 않고 호출 측이 직접 소비할 수
   * 있도록 보존한다. 파싱 실패/본문 없음/크기 상한 초과면 undefined.
   */
  body?: unknown;

  constructor(
    message: string,
    status: number,
    code?: string,
    isServerMessage: boolean = false,
    body?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.isServerMessage = isServerMessage;
    this.body = body;
  }
}

interface RequestOptions extends Omit<RequestInit, 'headers'> {
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  timeout?: number;
  /** Resource credentials can fail without expiring the customer's session. */
  authFailureScope?: 'session' | 'resource';
}

interface ReadRequestOptions extends RequestOptions {
  diagnosticOperation?: SafeReadOperation;
}

/**
 * API 응답 메타데이터
 * Content-Language 헤더 등 응답 메타 정보 포함
 */
export interface ApiResponseMeta {
  /** 응답에 사용된 언어 (Content-Language 헤더) */
  contentLanguage?: string;
  /** 요청한 언어와 응답 언어가 다른지 여부 */
  isLanguageFallback?: boolean;
}

/**
 * 메타데이터를 포함한 API 응답
 */
export interface ApiResponseWithMeta<T> {
  data: T;
  meta: ApiResponseMeta;
}

/**
 * API 클라이언트 초기 로케일 조회.
 * 저장된 사용자 선택이 없으면 AppContext 와 동일하게 브라우저 언어를 감지한다.
 */
function getInitialClientLocale(): Locale {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isValidLocale(stored)) {
      return stored;
    }
  } catch (error) {
    logger.warn('[ApiClient] Failed to read locale from localStorage:', error);
  }

  return detectBrowserLocale();
}

/**
 * 401 에러 핸들러 타입
 * API 요청 중 401 응답을 받았을 때 호출되는 콜백
 */
type UnauthorizedHandler = () => void;

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;
  private locale: Locale;
  private csrfInitialized: boolean = false;
  private csrfPending: Promise<void> | null = null;
  private csrfGeneration = 0;
  private csrfController: AbortController | null = null;
  private authEpoch = 0;
  private authIdentityEpoch = 0;
  private onUnauthorized: UnauthorizedHandler | null = null;

  constructor(baseUrl: string = API_BASE_URL || '') {
    this.baseUrl = baseUrl;
    this.locale = getInitialClientLocale();
  }

  /**
   * 401 에러 핸들러 등록
   * 세션 만료 시 자동 로그아웃 처리에 사용
   */
  setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
    this.onUnauthorized = handler;
  }

  /**
   * 로케일 설정 (Accept-Language 헤더에 사용)
   * @see SPEC-192-i18n-api.md
   */
  setLocale(locale: Locale): void {
    this.locale = locale;
  }

  /**
   * 현재 로케일 조회
   */
  getLocale(): Locale {
    return this.locale;
  }

  /**
   * 인증 토큰 설정 (Firebase ID Token 등)
   * 인메모리에서만 관리 (Firebase browserLocalPersistence가 토큰 지속성 담당)
   */
  setAuthToken(token: string | null): void {
    this.authToken = token;
    this.invalidateAuthScope();
  }

  /** Cookie-only identity changes also invalidate old requests' 401 side effects. */
  invalidateAuthScope({ preserveIdentity = false }: { preserveIdentity?: boolean } = {}): void {
    this.authEpoch += 1;
    if (!preserveIdentity) this.authIdentityEpoch += 1;
  }

  /** Snapshot an individual read's authentication scope without retaining token values. */
  getAuthEpoch(): number {
    return this.authEpoch;
  }

  /** Logout invalidates reads immediately, but still owns cleanup until its session ends. */
  getAuthIdentityEpoch(): number {
    return this.authIdentityEpoch;
  }

  /**
   * 인증 토큰 존재 여부 확인 (토큰 값 노출 방지)
   */
  hasAuthToken(): boolean {
    return this.authToken !== null;
  }

  /**
   * Sanctum CSRF 쿠키 초기화
   * SPA 인증을 위해 첫 요청 전에 호출해야 함
   *
   * @see https://laravel.com/docs/sanctum#spa-authentication
   * @see #129 - Firebase 인증 API 연동
   */
  async initCsrf(): Promise<void> {
    if (this.csrfInitialized) return;
    if (this.csrfPending) return this.csrfPending;
    const generation = this.csrfGeneration;
    const controller = new AbortController();
    this.csrfController = controller;
    const pending = this.fetchWithTimeout(
      `${this.baseUrl}/sanctum/csrf-cookie`,
      { method: 'GET', headers: { Accept: 'application/json' }, redirect: 'error', signal: controller.signal },
      DEFAULT_TIMEOUT,
      async (response, signal) => {
        this.assertNotAborted(signal);
        if (response.status !== 204 || response.redirected) {
          throw new ApiClientError(
            getStatusMessage(response.status) ?? getGenericFallbackMessage(),
            response.status,
            'CSRF_INIT_ERROR',
          );
        }
        if (this.csrfGeneration !== generation) {
          throw new DOMException('CSRF initialization superseded', 'AbortError');
        }
        this.csrfInitialized = true;
      },
    );
    this.csrfPending = pending;
    try {
      await pending;
    } finally {
      if (this.csrfPending === pending) this.csrfPending = null;
      if (this.csrfController === controller) this.csrfController = null;
    }
  }

  /**
   * CSRF 초기화 상태 리셋 (로그아웃 시 사용)
   */
  resetCsrf(): void {
    this.csrfInitialized = false;
    this.csrfGeneration += 1;
    this.csrfController?.abort();
    this.csrfController = null;
    this.csrfPending = null;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * Content-Language 헤더 파싱 및 언어 불일치 확인
   * @see Issue #47 - Content-Language 응답 헤더 확인 로직
   */
  private parseContentLanguage(response: Response, requestedLanguage: string | null): ApiResponseMeta {
    const contentLanguage = response.headers.get('Content-Language');
    const meta: ApiResponseMeta = {};

    if (contentLanguage) {
      // Content-Language 헤더에서 첫 번째 언어 코드 추출 (예: "es, en" -> "es")
      const responseLang = contentLanguage.split(',')[0].trim().toLowerCase();
      meta.contentLanguage = responseLang;

      // 요청 언어와 응답 언어 비교 (BCP 47 base subtag 매칭).
      // 'es' 요청 / 'es-mx' 응답 등 sub-locale 차이는 fallback 아님 (정상 매칭).
      const requestedLang = requestedLanguage?.trim().toLowerCase();
      // Compare only usable language tags from the actual dispatched header.
      if (!requestedLang || !/^[a-z]{2,3}(?:-[a-z0-9]{1,8})*$/.test(requestedLang)) return meta;
      const requestedBase = requestedLang.split('-')[0];
      const responseBase = responseLang.split('-')[0];
      const responseTagValid = /^[a-z]{2,3}(?:-[a-z0-9]{1,8})*$/.test(responseLang);
      if (!responseTagValid || requestedBase !== responseBase) {
        meta.isLanguageFallback = true;
        // Unknown/malformed server languages remain observable without logging
        // arbitrary request or provider-reflected header text.
        const requestedLabel = isValidLocale(requestedBase) ? requestedBase : 'unsupported';
        const responseLabel = !responseTagValid ? 'invalid' : isValidLocale(responseBase) ? responseBase : 'unsupported';
        logger.warn(`[ApiClient] Language fallback: requested '${requestedLabel}', received '${responseLabel}'`, {
          diagnostic_marker: 'menu.api.locale-mismatch.v1',
          operation: 'response-language',
          requested_locale: requestedLabel,
          received_locale: responseLabel,
        });
      } else {
        meta.isLanguageFallback = false;
      }
    }

    return meta;
  }

  /**
   * 에러 응답 JSON 에서 message / code 를 추출 (양 envelope 정합).
   *
   * admin BE 는 두 가지 에러 envelope 를 혼용한다:
   *   1. flat — `{ message, error_code }` (컨트롤러 가드: PAYMENT_NOT_FOUND 등)
   *   2. nested — `{ error: { code, message } }` (PaymentException::toJsonResponse:
   *      PAYMENT_NOT_SETTLEABLE 등 — admin#1705 A2 /charge)
   *
   * 두 shape 모두에서 code / message 를 robust 하게 추출한다. flat 응답 무회귀 (additive).
   *
   * @returns 추출된 message (서버 제공 시) 와 code. message 없으면 serverMessage undefined.
   */
  private extractServerError(errorData: unknown): { serverMessage?: string; code?: string } {
    if (typeof errorData !== 'object' || errorData === null) {
      return {};
    }

    const data = errorData as {
      message?: unknown;
      error?: unknown;
      code?: unknown;
      error_code?: unknown;
    };

    const nestedError =
      typeof data.error === 'object' && data.error !== null
        ? (data.error as { code?: unknown; message?: unknown })
        : null;

    // message: flat `message` → nested `error.message` → string `error` fallback.
    let serverMessage: string | undefined;
    if (typeof data.message === 'string') {
      serverMessage = data.message;
    } else if (nestedError && typeof nestedError.message === 'string') {
      serverMessage = nestedError.message;
    } else if (typeof data.error === 'string') {
      serverMessage = data.error;
    }

    // code: flat `code`/`error_code` → nested `error.code`.
    let code: string | undefined;
    if (typeof data.code === 'string') {
      code = data.code;
    } else if (typeof data.error_code === 'string') {
      code = data.error_code;
    } else if (nestedError && typeof nestedError.code === 'string') {
      code = nestedError.code;
    }

    return { serverMessage, code };
  }

  private isJsonContentType(value: string | null): boolean {
    const mime = value?.split(';', 1)[0].trim().toLowerCase();
    return mime === 'application/json' || /^application\/[a-z0-9!#$&^_.+-]+\+json$/.test(mime ?? '');
  }

  private assertNotAborted(signal?: AbortSignal | null): void {
    if (signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
  }

  private async parseSuccessBody<T>(response: Response, signal?: AbortSignal | null, partyRead = false): Promise<T> {
    if (!this.isJsonContentType(response.headers.get('content-type'))) {
      const failure = new ApiClientError(getParseFallbackMessage(), response.status, 'PARSE_ERROR');
      annotateParseFailure(failure, response, 'content-type');
      throw failure;
    }
    let text: string | undefined;
    try {
      // 상태 조회는 본문을 한 번만 읽고 길이/빈 본문만 분류한다. 원문은 보관하지 않는다.
      const data = partyRead ? JSON.parse(text = await response.text()) : await response.json();
      this.assertNotAborted(signal);
      return data;
    } catch (error) {
      this.assertNotAborted(signal);
      if (error instanceof TypeError || (error instanceof DOMException && error.name === 'AbortError')) {
        throw error;
      }
      const failure = new ApiClientError(getParseFallbackMessage(), response.status, 'PARSE_ERROR');
      annotateParseFailure(failure, response, error, text);
      // 복구 가능한 대기 조회는 최종 실패를 hook에서 한 번 보고한다.
      if (!partyRead) logger.error('Failed to parse success response:', { status: response.status, code: 'PARSE_ERROR' });
      throw failure;
    }
  }

  private async handleResponse<T>(response: Response, signal?: AbortSignal | null, authEpoch = this.authEpoch, authFailureScope: 'session' | 'resource' = 'session', requestedLanguage: string | null = null, partyRead = false): Promise<T> {
    this.assertNotAborted(signal);
    if (!response.ok) {
      let errorMessage = response.statusText || getGenericFallbackMessage();
      let errorCode: string | undefined;
      let isServerMessage = false;
      let errorBody: unknown;

      try {
        const errorData = await response.json();
        errorBody = capErrorBody(errorData); // #674 — 구조화 payload 보존 (크기 상한 캡)
        const { serverMessage, code } = this.extractServerError(errorData);
        if (serverMessage) {
          errorMessage = serverMessage;
          isServerMessage = true;
        }
        errorCode = code;
      } catch {
        this.assertNotAborted(signal);
        logger.error('Failed to parse error response:', { status: response.status, code: 'PARSE_ERROR' });
      }

      // BE 가 Accept-Language 무시하고 Korean stock 메시지를 보낼 수 있음.
      // 알려진 상태 코드(401/403/404/등)는 FE i18n 으로 강제 override —
      // 사용자 언어 일관성 확보.
      const localized = getStatusMessage(response.status);
      if (localized) {
        errorMessage = localized;
        isServerMessage = false;
      } else if (isServerMessage && HANGUL_REGEX.test(errorMessage)) {
        // 비매핑 status (422 등) 에서 BE 가 한국어 message 를 내려준 경우 —
        // 사용자 언어 컨텍스트 누설 차단. generic fallback 으로 대체.
        // (codex 1R P1 시정: #380 stage 2 후속)
        errorMessage = getGenericFallbackMessage();
        isServerMessage = false;
      }

      this.assertNotAborted(signal);
      // 401 에러 시 등록된 핸들러 호출 (자동 로그아웃)
      if (authFailureScope === 'session' && response.status === 401 && authEpoch === this.authEpoch && this.onUnauthorized) {
        this.onUnauthorized();
      }

      throw new ApiClientError(errorMessage, response.status, errorCode, isServerMessage, errorBody);
    }

    // Content-Language 헤더 파싱 (Issue #47)
    this.parseContentLanguage(response, requestedLanguage);

    // 204 No Content인 경우 빈 객체 반환
    if (response.status === 204) {
      return {} as T;
    }

    return this.parseSuccessBody<T>(response, signal, partyRead);
  }

  /**
   * 응답 데이터와 메타데이터를 함께 반환하는 핸들러
   * Content-Language 정보가 필요한 경우 사용
   * @see Issue #47
   */
  private async handleResponseWithMeta<T>(response: Response, signal?: AbortSignal | null, authEpoch = this.authEpoch, authFailureScope: 'session' | 'resource' = 'session', requestedLanguage: string | null = null, partyRead = false): Promise<ApiResponseWithMeta<T>> {
    this.assertNotAborted(signal);
    if (!response.ok) {
      let errorMessage = response.statusText || getGenericFallbackMessage();
      let errorCode: string | undefined;
      let isServerMessage = false;
      let errorBody: unknown;

      try {
        const errorData = await response.json();
        errorBody = capErrorBody(errorData); // #674 — 구조화 payload 보존 (handleResponse 와 동일 패턴)
        const { serverMessage, code } = this.extractServerError(errorData);
        if (serverMessage) {
          errorMessage = serverMessage;
          isServerMessage = true;
        }
        errorCode = code;
      } catch {
        this.assertNotAborted(signal);
        logger.error('Failed to parse error response:', { status: response.status, code: 'PARSE_ERROR' });
      }

      // 동일 패턴: 알려진 상태 코드는 FE i18n override.
      const localizedMeta = getStatusMessage(response.status);
      if (localizedMeta) {
        errorMessage = localizedMeta;
        isServerMessage = false;
      } else if (isServerMessage && HANGUL_REGEX.test(errorMessage)) {
        // 비매핑 status 한국어 server message 차단 (codex 1R P1)
        errorMessage = getGenericFallbackMessage();
        isServerMessage = false;
      }

      this.assertNotAborted(signal);
      // 401 에러 시 등록된 핸들러 호출 (자동 로그아웃)
      if (authFailureScope === 'session' && response.status === 401 && authEpoch === this.authEpoch && this.onUnauthorized) {
        this.onUnauthorized();
      }

      throw new ApiClientError(errorMessage, response.status, errorCode, isServerMessage, errorBody);
    }

    // Content-Language 헤더 파싱
    const meta = this.parseContentLanguage(response, requestedLanguage);

    // 204 No Content
    if (response.status === 204) {
      return { data: {} as T, meta };
    }

    return { data: await this.parseSuccessBody<T>(response, signal, partyRead), meta };
  }

  private buildHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Accept-Language': this.locale,
    };

    // 인증 토큰이 있으면 Authorization 헤더 추가
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    // #364/#367/#1430: 테이블 세션 토큰 헤더 자동 부착.
    // 세 분기 (위에서부터 우선 적용):
    //   0. checkout route 에서는 checkout 전용 resolver 로 3 키 + URL table 교차검증
    //   1. path `/table/{n}` 매칭 + 유효 토큰 (메뉴/카트 모달 흐름 — #367 박제)
    //   2. sessionStorage 3 키 (token + table_number + table_store_name) 모두 존재
    //      (체크아웃 흐름 — #1430 root cause: /checkout path 에서 1 분기 실패).
    // 둘 다 stale token 오염 방지 — PickupMenu 가 픽업 매장 진입 시 table_number/store_name clear.
    // 단일 출처: src/lib/tableContext.ts (박제 D)
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      try {
        const sessionToken = window.sessionStorage.getItem('table_session_token');
        if (isCheckoutRoutePath(pathname)) {
          const checkoutSession = getCheckoutTableSessionFromStorage(pathname);
          if (checkoutSession) {
            headers['X-Session-Token'] = checkoutSession.token;
          }
        } else if (sessionToken && isValidTableContext(pathname, sessionToken)) {
          headers['X-Session-Token'] = sessionToken;
        } else {
          const checkoutSession = getTableSessionFromStorage();
          if (checkoutSession) {
            headers['X-Session-Token'] = checkoutSession.token;
          }
        }
      } catch {
        // Account/public API requests can still use cookies or bearer auth. Table
        // operations must fail closed when their stored context cannot be read.
        if (isCheckoutRoutePath(pathname) || /(?:^|\/)table(?:\/|$)/.test(pathname)) {
          throw new ApiClientError(getGenericFallbackMessage(), 0, 'STORAGE_UNAVAILABLE');
        }
      }
    }

    // 사용자 정의 헤더가 기본 헤더를 덮어씀
    if (customHeaders) {
      for (const [name, value] of Object.entries(customHeaders)) {
        // Header names are case-insensitive; avoid combining a custom locale
        // with the default under two differently cased record keys.
        headers[name.toLowerCase() === 'accept-language' ? 'Accept-Language' : name] = value;
      }
    }

    return headers;
  }

  private async fetchWithTimeout<T>(
    url: string,
    options: RequestInit,
    timeout: number = DEFAULT_TIMEOUT,
    parse: (response: Response, signal: AbortSignal, authEpoch: number, requestedLanguage: string | null) => Promise<T>,
    diagnosticOperation?: SafeReadOperation,
  ): Promise<T> {
    const authEpoch = this.authEpoch;
    const controller = new AbortController();
    const externalSignal = options.signal;
    let abortCause: 'timeout' | 'external' | null = null;
    const started = performance.now();
    let phase: TransportPhase = 'fetch';
    let responseStatus: number | undefined;
    let requestId: string | undefined;

    const abort = (cause: 'timeout' | 'external') => {
      if (controller.signal.aborted) return;
      abortCause = cause;
      controller.abort();
    };

    let rejectAbort!: (error: DOMException) => void;
    const aborted = new Promise<never>((_resolve, reject) => { rejectAbort = reject; });
    const handleInternalAbort = () => rejectAbort(new DOMException('Request aborted', 'AbortError'));
    controller.signal.addEventListener('abort', handleInternalAbort, { once: true });
    const handleExternalAbort = () => abort('external');
    const timeoutId = setTimeout(() => abort('timeout'), timeout);

    if (externalSignal) {
      if (externalSignal.aborted) {
        abort('external');
      } else {
        externalSignal.addEventListener('abort', handleExternalAbort, { once: true });
      }
    }

    try {
      const requestHeaders = new Headers(options.headers);
      const diagnosticId = requestHeaders.get(READ_REQUEST_ID_HEADER);
      requestId = isReadRequestId(diagnosticId) ? diagnosticId : undefined;
      const requestedLanguage = requestHeaders.get('Accept-Language');
      const response = await Promise.race([fetch(url, {
        ...options,
        credentials: 'include', // Sanctum SPA 인증을 위한 쿠키 포함 (#129)
        signal: controller.signal,
      }), aborted]);
      responseStatus = response.status;
      phase = 'response-body';
      this.assertNotAborted(controller.signal);
      return await Promise.race([parse(response, controller.signal, authEpoch, requestedLanguage), aborted]);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError' && abortCause !== null) {
        if (abortCause === 'external') {
          throw error;
        }

        // #380 Stage 2 — 박제 D 단일 출처 (error-messages.ts)
        const failure = new ApiClientError(
          getStatusMessage(408) ?? 'La solicitud tardó demasiado tiempo',
          408,
          'TIMEOUT',
        );
        annotateTransportFailure(failure, phase, 'deadline', performance.now() - started, responseStatus, requestId);
        throw failure;
      }
      if (error instanceof TypeError || (error instanceof DOMException && error.name === 'AbortError')) {
        // An unsolicited transport abort is not proof that our deadline fired.
        // #380 Stage 2 — 박제 D 단일 출처 (error-messages.ts)
        const failure = new ApiClientError(
          getStatusMessage(0) ?? 'Verifica tu conexión a internet',
          0,
          'NETWORK_ERROR',
        );
        annotateTransportFailure(failure, phase, error instanceof TypeError ? 'TypeError' : 'AbortError', performance.now() - started, responseStatus, requestId);
        throw failure;
      }
      if (error instanceof ApiClientError && error.code === 'PARSE_ERROR') {
        annotateParseRequest(error, options.method, performance.now() - started, requestId);
        if (options.method === 'GET' && diagnosticOperation === 'party-read') {
          recordPartyParseFailure(error);
        }
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', handleExternalAbort);
      controller.signal.removeEventListener('abort', handleInternalAbort);
    }
  }

  async get<T>(endpoint: string, options: ReadRequestOptions = {}): Promise<T> {
    const { params, headers, timeout, authFailureScope, diagnosticOperation, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    return this.fetchWithTimeout(
      url,
      {
        method: 'GET',
        headers: this.buildHeaders(headers),
        ...fetchOptions,
      },
      timeout,
      (response, signal, authEpoch, requestedLanguage) => this.handleResponse<T>(response, signal, authEpoch, authFailureScope, requestedLanguage, diagnosticOperation === 'party-read'),
      diagnosticOperation,
    );
  }

  async post<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, headers, timeout, authFailureScope, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    return this.fetchWithTimeout(
      url,
      {
        method: 'POST',
        headers: this.buildHeaders(headers),
        body: data ? JSON.stringify(data) : undefined,
        ...fetchOptions,
      },
      timeout,
      (response, signal, authEpoch, requestedLanguage) => this.handleResponse<T>(response, signal, authEpoch, authFailureScope, requestedLanguage),
    );
  }

  async put<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, headers, timeout, authFailureScope, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    return this.fetchWithTimeout(
      url,
      {
        method: 'PUT',
        headers: this.buildHeaders(headers),
        body: data ? JSON.stringify(data) : undefined,
        ...fetchOptions,
      },
      timeout,
      (response, signal, authEpoch, requestedLanguage) => this.handleResponse<T>(response, signal, authEpoch, authFailureScope, requestedLanguage),
    );
  }

  async patch<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, headers, timeout, authFailureScope, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    return this.fetchWithTimeout(
      url,
      {
        method: 'PATCH',
        headers: this.buildHeaders(headers),
        body: data ? JSON.stringify(data) : undefined,
        ...fetchOptions,
      },
      timeout,
      (response, signal, authEpoch, requestedLanguage) => this.handleResponse<T>(response, signal, authEpoch, authFailureScope, requestedLanguage),
    );
  }

  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, headers, timeout, authFailureScope, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    return this.fetchWithTimeout(
      url,
      {
        method: 'DELETE',
        headers: this.buildHeaders(headers),
        ...fetchOptions,
      },
      timeout,
      (response, signal, authEpoch, requestedLanguage) => this.handleResponse<T>(response, signal, authEpoch, authFailureScope, requestedLanguage),
    );
  }

  /**
   * GET 요청 + Content-Language 메타데이터 반환
   * 언어 fallback 정보가 필요한 경우 사용
   * @see Issue #47
   */
  async getWithMeta<T>(endpoint: string, options: ReadRequestOptions = {}): Promise<ApiResponseWithMeta<T>> {
    const { params, headers, timeout, authFailureScope, diagnosticOperation, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    return this.fetchWithTimeout(
      url,
      {
        method: 'GET',
        headers: this.buildHeaders(headers),
        ...fetchOptions,
      },
      timeout,
      (response, signal, authEpoch, requestedLanguage) => this.handleResponseWithMeta<T>(response, signal, authEpoch, authFailureScope, requestedLanguage, diagnosticOperation === 'party-read'),
      diagnosticOperation,
    );
  }
}

// 싱글톤 인스턴스
export const apiClient = new ApiClient();

// 클래스도 export하여 테스트 등에서 새 인스턴스 생성 가능
export { ApiClient };
